import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { notificationService } from '../../services/notificationService';
import NotificationPanel from './NotificationPanel';

const PrimaryNavigation = () => {
  const location = useLocation();
  const { userProfile, user } = useAuth();
  const [notificationCount, setNotificationCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const channelRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load notification count
  useEffect(() => {
    if (user?.id) {
      loadNotificationCount();

      // Clean up any existing channel before creating a new one
      if (channelRef?.current) {
        notificationService?.unsubscribeFromNotifications(channelRef?.current);
        channelRef.current = null;
      }

      // Subscribe to real-time notifications
      channelRef.current = notificationService?.subscribeToNotifications(user?.id, () => {
        loadNotificationCount();
      });

      return () => {
        if (channelRef?.current) {
          notificationService?.unsubscribeFromNotifications(channelRef?.current);
          channelRef.current = null;
        }
      };
    }
  }, [user?.id]);

  const loadNotificationCount = async () => {
    try {
      const count = await notificationService?.getUnreadCount(user?.id);
      setNotificationCount(count);
    } catch (error) {
      console.error('Error loading notification count:', error);
    }
  };

  // Determine the correct homepage path based on user role
  const getHomePagePath = () => {
    if (!userProfile?.role) return '/homepage';
    return userProfile?.role === 'boss' ? '/boss-home-page' : '/worker-home-page';
  };

  const navigationItems = [
    {
      label: 'Projects',
      path: getHomePagePath(),
      icon: 'LayoutDashboard',
      tooltip: 'View all projects'
    }
  ];

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  const isActive = (path) => {
    return location?.pathname === path;
  };

  if (isMobile) {
    return (
      <nav className="fixed bottom-0 left-0 right-0 bg-card border-t border-border z-50 safe-area-bottom">
        <div className="flex items-center justify-around h-16 px-4">
          {navigationItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              className={`flex flex-col items-center justify-center min-w-[48px] min-h-[48px] px-3 py-2 rounded-lg transition-smooth relative ${
                isActive(item?.path)
                  ? 'text-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              aria-label={item?.tooltip}
            >
              <Icon 
                name={item?.icon} 
                size={24} 
                strokeWidth={isActive(item?.path) ? 2.5 : 2}
              />
              <span className={`text-xs mt-1 font-medium ${isActive(item?.path) ? 'font-semibold' : ''}`}>
                {item?.label}
              </span>
              {item?.badge && item?.badge > 0 && (
                <span className="absolute top-1 right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold text-error-foreground bg-error rounded-full">
                  {item?.badge > 99 ? '99+' : item?.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      </nav>
    );
  }

  return (
    <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-50">
      <div className="flex items-center justify-between h-20 px-4 md:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Link to="/homepage" className="flex items-center gap-3 transition-smooth hover:opacity-80">
            <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
              <Icon name="HardHat" size={24} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <span className="text-xl font-semibold text-foreground">RenovationFlow</span>
          </Link>

        </div>

        <div className="flex items-center gap-4">
          <NotificationPanel />
        </div>
      </div>
    </header>
  );
};

export default PrimaryNavigation;